'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  clearAuthUser,
  getAuthUser,
  getMe,
  login as loginRequest,
  logout as logoutRequest,
  resendLogin2fa as resendLogin2faRequest,
  saveAuthUser,
  verifyLogin2fa as verifyLogin2faRequest,
  type AuthUser,
  type Login2faChallenge,
  type LoginResult,
} from '@/lib/auth-api';
import { hasTabScopedSession } from '@/lib/api';
import {
  DEFAULT_AUTHENTICATED_PATH,
  canAccessPath,
  getFirstAllowedPath,
  getSafeNextPath,
  hasPermission,
  isGuestOnlyRoute,
  isProtectedRoute,
} from '@/lib/auth-config';
import { ApiError } from '@/lib/api';
import { SessionExpiredModal } from '@/components/ui/SessionExpiredModal';
import { fetchSecuritySetting } from '@/lib/settings-api';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

type AuthContextValue = {
  user: AuthUser | null;
  status: AuthStatus;
  isAuthenticated: boolean;
  hasPermission: (slug: string | string[] | null | undefined) => boolean;
  canAccessPath: (pathname: string) => boolean;
  refresh: () => Promise<AuthUser | null>;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyLogin2fa: (params: {
    challenge_id: string;
    email: string;
    otp: string;
  }) => Promise<AuthUser>;
  resendLogin2fa: (params: {
    challenge_id: string;
    email: string;
  }) => Promise<Login2faChallenge>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthLoadingScreen({ label = 'Checking session...' }: { label?: string }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 size={28} className="animate-spin text-primary" />
        <p className="text-sm font-500">{label}</p>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [sessionExpired, setSessionExpired] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const verifiedRef = useRef(false);
  const forbiddenToastRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const result = await getMe();
      const nextUser = result.data?.user || null;
      if (!nextUser) {
        console.log('[Auth] getMe returned no user payload');
        clearAuthUser();
        setUser(null);
        setStatus('unauthenticated');
        return null;
      }
      console.log(`[Auth] getMe success: ${nextUser.email} (role: ${nextUser.role?.slug})`);
      saveAuthUser(nextUser);
      setUser(nextUser);
      setStatus('authenticated');
      return nextUser;
    } catch (error) {
      console.warn(`[Auth] getMe error on ${pathname}:`, error);
      // Only real auth failures should log the user out
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        console.warn(`[Auth] 401/403 received on ${pathname} -> Setting unauthenticated and sessionExpired`);
        clearAuthUser();
        setUser(null);
        setStatus('unauthenticated');
        if (isProtectedRoute(pathname)) {
          setSessionExpired(true);
        }
        return null;
      }
      const cached = getAuthUser();
      if (cached) {
        console.log('[Auth] Using cached user data during network error:', cached.email);
        setUser(cached);
        setStatus('authenticated');
        return cached;
      }
      return null;
    }
  }, [pathname]);

  // Configured session timeout in minutes (defaults to 60 or loaded from settings)
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState<number>(() => {
    if (typeof window === 'undefined') return 60;
    try {
      const saved = localStorage.getItem('dfms_session_timeout_minutes');
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed) && parsed > 0) return parsed;
      }
    } catch {}
    return 60;
  });

  const lastActivityRef = useRef<number>(Date.now());
  const lastHeartbeatRef = useRef<number>(Date.now());

  // Listen for session timeout settings updates
  useEffect(() => {
    const handleTimeoutUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ session_timeout_minutes: number }>;
      const minutes = customEvent?.detail?.session_timeout_minutes;
      if (minutes && typeof minutes === 'number' && minutes > 0) {
        console.log(`[Auth] Session timeout updated via event: ${minutes} min`);
        setSessionTimeoutMinutes(minutes);
        localStorage.setItem('dfms_session_timeout_minutes', String(minutes));
      }
    };

    window.addEventListener('dfms:session-timeout-updated', handleTimeoutUpdated);
    return () => {
      window.removeEventListener('dfms:session-timeout-updated', handleTimeoutUpdated);
    };
  }, []);

  // Fetch security settings on authentication to sync latest session timeout
  useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;

    fetchSecuritySetting()
      .then((sec) => {
        if (!cancelled && sec?.session_timeout_minutes) {
          console.log(`[Auth] Synced session timeout from backend: ${sec.session_timeout_minutes} min`);
          setSessionTimeoutMinutes(sec.session_timeout_minutes);
          try {
            localStorage.setItem('dfms_session_timeout_minutes', String(sec.session_timeout_minutes));
          } catch {}
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [status]);

  // Track user activity (mouse, keyboard, touch, scroll)
  useEffect(() => {
    if (status !== 'authenticated' || !isProtectedRoute(pathname)) return;

    let throttleTimer: NodeJS.Timeout | null = null;
    const handleUserActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        try {
          localStorage.setItem('dfms_last_activity_ts', String(Date.now()));
        } catch {}
        throttleTimer = null;
      }, 1000);
    };

    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'pointerdown',
      'click',
    ];

    activityEvents.forEach((evt) => {
      window.addEventListener(evt, handleUserActivity, { passive: true });
    });

    return () => {
      if (throttleTimer) clearTimeout(throttleTimer);
      activityEvents.forEach((evt) => {
        window.removeEventListener(evt, handleUserActivity);
      });
    };
  }, [status, pathname]);

  // Listen for global 401 session expiry events
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      if (isProtectedRoute(pathname)) {
        console.warn(`[Auth] Global session-expired event received on ${pathname}`);
        clearAuthUser();
        setUser(null);
        setStatus('unauthenticated');
        setSessionExpired(true);
      }
    };

    window.addEventListener('flipbook:session-expired', handleSessionExpiredEvent);
    return () => {
      window.removeEventListener('flipbook:session-expired', handleSessionExpiredEvent);
    };
  }, [pathname]);

  // Proactive Idle Activity & Session Timeout Checker (runs every 2s)
  useEffect(() => {
    if (status !== 'authenticated' || !isProtectedRoute(pathname)) return;

    const interval = setInterval(async () => {
      const now = Date.now();
      let lastActive = lastActivityRef.current;
      try {
        const stored = Number(localStorage.getItem('dfms_last_activity_ts'));
        if (stored && Number.isFinite(stored) && stored > lastActive) {
          lastActive = stored;
          lastActivityRef.current = stored;
        }
      } catch {}

      const idleMs = now - lastActive;
      const timeoutMs = (sessionTimeoutMinutes || 60) * 60 * 1000;

      // 1. If user has been inactive for >= session timeout, expire session immediately
      if (idleMs >= timeoutMs) {
        console.warn(`[Auth] Inactivity timeout triggered: idle ${Math.round(idleMs / 1000)}s >= limit ${Math.round(timeoutMs / 1000)}s`);
        clearAuthUser();
        setUser(null);
        setStatus('unauthenticated');
        setSessionExpired(true);
        return;
      }

      // 2. Periodic background heartbeat verification (every 60s)
      if (now - lastHeartbeatRef.current >= 60 * 1000) {
        lastHeartbeatRef.current = now;
        try {
          await getMe();
        } catch (error) {
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            console.warn('[Auth] Background heartbeat 401/403 -> Expiring session');
            clearAuthUser();
            setUser(null);
            setStatus('unauthenticated');
            setSessionExpired(true);
          }
        }
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [status, pathname, sessionTimeoutMinutes]);

  useEffect(() => {
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    let cancelled = false;

    (async () => {
      const cached = getAuthUser();
      if (cached && !cancelled) {
        setUser(cached);
        setStatus('authenticated');
      }

      try {
        const result = await getMe();
        if (cancelled) return;
        const nextUser = result.data?.user || null;
        if (!nextUser) {
          clearAuthUser();
          setUser(null);
          setStatus('unauthenticated');
          return;
        }
        saveAuthUser(nextUser);
        setUser(nextUser);
        setStatus('authenticated');
      } catch (error) {
        if (cancelled) return;
        if (
          error instanceof ApiError &&
          (error.status === 401 || error.status === 403)
        ) {
          clearAuthUser();
          setUser(null);
          setStatus('unauthenticated');
          return;
        }
        // Transient network/backend error — keep cached session if present
        if (cached) {
          setUser(cached);
          setStatus('authenticated');
        } else {
          setStatus('unauthenticated');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignInAgain = useCallback(() => {
    setSessionExpired(false);
    clearAuthUser();
    setUser(null);
    setStatus('unauthenticated');
    const next = `${pathname}${searchParams?.toString() ? `?${searchParams}` : ''}`;
    logoutRequest().catch(() => {});
    if (typeof window !== 'undefined') {
      window.location.href = `/?next=${encodeURIComponent(next)}`;
    } else {
      router.replace(`/?next=${encodeURIComponent(next)}`);
    }
  }, [pathname, router, searchParams]);

  useEffect(() => {
    if (status === 'loading') return;

    if (status === 'unauthenticated' && isProtectedRoute(pathname)) {
      if (sessionExpired) return; // Wait for user interaction on session expired dialog
      const next = `${pathname}${searchParams?.toString() ? `?${searchParams}` : ''}`;
      router.replace(`/?next=${encodeURIComponent(next)}`);
      return;
    }

    if (status === 'authenticated' && isGuestOnlyRoute(pathname)) {
      const nextParam = searchParams?.get('next');
      const safeNext = getSafeNextPath(nextParam, DEFAULT_AUTHENTICATED_PATH);
      const destination = canAccessPath(user, safeNext)
        ? safeNext
        : getFirstAllowedPath(user, DEFAULT_AUTHENTICATED_PATH);
      if (typeof window !== 'undefined') {
        window.location.replace(destination);
      } else {
        router.replace(destination);
      }
      return;
    }

    if (
      status === 'authenticated' &&
      isProtectedRoute(pathname) &&
      !canAccessPath(user, pathname)
    ) {
      if (!forbiddenToastRef.current) {
        forbiddenToastRef.current = true;
        toast.error("You don't have permission to access that page");
      }
      router.replace(getFirstAllowedPath(user, DEFAULT_AUTHENTICATED_PATH));
    }
  }, [status, pathname, router, searchParams, user, sessionExpired]);

  useEffect(() => {
    if (searchParams?.get('forbidden') === '1' && !forbiddenToastRef.current) {
      forbiddenToastRef.current = true;
      toast.error("You don't have permission to access that page");
      const params = new URLSearchParams(searchParams.toString());
      params.delete('forbidden');
      const query = params.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
    }
  }, [searchParams, pathname, router]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await loginRequest(email, password);
    if (result.requires_2fa) {
      return result;
    }
    saveAuthUser(result.user);
    setUser(result.user);
    setStatus('authenticated');
    return result;
  }, []);

  const verifyLogin2fa = useCallback(
    async (params: { challenge_id: string; email: string; otp: string }) => {
      const result = await verifyLogin2faRequest(params);
      const nextUser = result.data?.user;
      if (!nextUser) {
        throw new ApiError('Login succeeded but no user was returned', 500);
      }
      saveAuthUser(nextUser);
      setUser(nextUser);
      setStatus('authenticated');
      return nextUser;
    },
    []
  );

  const resendLogin2fa = useCallback(
    async (params: { challenge_id: string; email: string }) => {
      const result = await resendLogin2faRequest(params);
      const data = result.data;
      if (!data?.challenge_id) {
        throw new ApiError('Failed to resend login OTP', 500);
      }
      return {
        challenge_id: data.challenge_id,
        email_hint: data.email_hint || params.email,
        email: data.email || params.email,
        expires_in_seconds: data.expires_in_seconds || 180,
        remaining_sends: data.remaining_sends,
        emailed: data.emailed,
      };
    },
    []
  );

  const logout = useCallback(async () => {
    const wasTabScoped = hasTabScopedSession();
    try {
      await logoutRequest();
    } finally {
      clearAuthUser();
      if (wasTabScoped) {
        // Caller (AdminLayout) closes this tab — keep status stable briefly.
        setUser(null);
        setStatus('unauthenticated');
        return;
      }
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isAuthenticated: status === 'authenticated',
      hasPermission: (slug) => hasPermission(user, slug),
      canAccessPath: (path) => canAccessPath(user, path),
      refresh,
      login,
      verifyLogin2fa,
      resendLogin2fa,
      logout,
    }),
    [user, status, refresh, login, verifyLogin2fa, resendLogin2fa, logout]
  );

  const shouldBlockProtected =
    isProtectedRoute(pathname) &&
    (status === 'loading' ||
      status === 'unauthenticated' ||
      (status === 'authenticated' && !canAccessPath(user, pathname)));

  const shouldBlockGuestRedirect =
    isGuestOnlyRoute(pathname) && status === 'authenticated';

  if (shouldBlockProtected || shouldBlockGuestRedirect) {
    return (
      <AuthContext.Provider value={value}>
        {sessionExpired && (
          <SessionExpiredModal
            isOpen={sessionExpired}
            onSignInAgain={handleSignInAgain}
          />
        )}
        <AuthLoadingScreen
          label={
            sessionExpired
              ? 'Session expired'
              : shouldBlockGuestRedirect
              ? 'Redirecting...'
              : status === 'authenticated' && !canAccessPath(user, pathname)
              ? 'Redirecting...'
              : 'Checking session...'
          }
        />
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiredModal
        isOpen={sessionExpired}
        onSignInAgain={handleSignInAgain}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
